/**
 * Filtros de Histórico (F6): fecha exacta, rango y mes se combinan sin contradecirse.
 * Histórico = mes anterior y más viejo. El mes actual vive en Movimientos / Cierre diario.
 * Elegir un mes llena desde–hasta. Elegir un día llena el rango a ese día.
 */
export type HistoricoDateFilter = {
  fecha: string;
  desde: string;
  hasta: string;
  mes: string;
};

const YMD = /^\d{4}-\d{2}-\d{2}$/;
const YM = /^\d{4}-\d{2}$/;

export function localYmd(d = new Date()): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function monthRange(mes: string): { desde: string; hasta: string } | null {
  if (!YM.test(mes)) return null;
  const [y, m] = mes.split("-").map(Number);
  if (m < 1 || m > 12) return null;
  const last = new Date(y, m, 0).getDate();
  const mm = String(m).padStart(2, "0");
  return {
    desde: `${y}-${mm}-01`,
    hasta: `${y}-${mm}-${String(last).padStart(2, "0")}`,
  };
}

/** YYYY-MM del mes calendario anterior a `todayYmd`. */
export function previousMonthYm(todayYmd: string): string {
  const [y, m] = todayYmd.slice(0, 7).split("-").map(Number);
  if (m === 1) return `${y - 1}-12`;
  return `${y}-${String(m - 1).padStart(2, "0")}`;
}

/** Último día del mes anterior (tope inclusive de Histórico). */
export function historicoHastaMax(todayYmd: string): string {
  const range = monthRange(previousMonthYm(todayYmd));
  return range?.hasta ?? "";
}

export function applyHistoricoFecha(
  prev: HistoricoDateFilter,
  fecha: string,
): HistoricoDateFilter {
  if (!fecha || !YMD.test(fecha)) {
    return { ...prev, fecha: "" };
  }
  return {
    fecha,
    desde: fecha,
    hasta: fecha,
    mes: fecha.slice(0, 7),
  };
}

export function applyHistoricoMes(mes: string): HistoricoDateFilter {
  const range = monthRange(mes);
  if (!range) {
    return { fecha: "", desde: "", hasta: "", mes: "" };
  }
  return {
    fecha: "",
    desde: range.desde,
    hasta: range.hasta,
    mes,
  };
}

export function applyHistoricoRango(desde: string, hasta: string): HistoricoDateFilter {
  let d = YMD.test(desde) ? desde : "";
  let h = YMD.test(hasta) ? hasta : "";
  if (d && h && d > h) {
    const swap = d;
    d = h;
    h = swap;
  }
  const fecha = d && h && d === h ? d : "";
  const mes =
    d && h && d.slice(0, 7) === h.slice(0, 7)
      ? d.slice(0, 7)
      : "";
  return { fecha, desde: d, hasta: h, mes };
}

/**
 * Recorta el filtro para que no incluya el mes actual.
 * Mes/día del mes actual caen al mes calendario anterior.
 */
export function clampHistoricoDateFilter(
  filter: HistoricoDateFilter,
  todayYmd: string,
): HistoricoDateFilter {
  const maxYm = previousMonthYm(todayYmd);
  const maxYmd = historicoHastaMax(todayYmd);
  if (!maxYmd) return filter;

  if (filter.mes && filter.mes > maxYm) {
    return applyHistoricoMes(maxYm);
  }

  let fecha = filter.fecha && filter.fecha <= maxYmd ? filter.fecha : "";
  let desde = filter.desde;
  let hasta = filter.hasta;

  if (hasta && hasta > maxYmd) hasta = maxYmd;
  if (desde && desde > maxYmd) {
    return applyHistoricoMes(maxYm);
  }
  if (desde && hasta && desde > hasta) {
    return applyHistoricoMes(maxYm);
  }
  if (fecha && desde && hasta && fecha === desde && fecha === hasta) {
    return { fecha, desde, hasta, mes: fecha.slice(0, 7) };
  }
  return applyHistoricoRango(desde, hasta);
}

export function defaultHistoricoDateFilter(todayYmd: string): HistoricoDateFilter {
  return applyHistoricoMes(previousMonthYm(todayYmd));
}

/** Tope de filas por fetch cuando sí se busca. La UI pagina de a 10. */
export const HISTORICO_PAGE_SIZE = 200;

/** Ítems por página en listados de Caja (Movimientos / Histórico). */
export const CAJA_LISTADO_PAGE_SIZE = 10;

export function paginateListado<T>(
  items: T[],
  page: number,
  pageSize: number = CAJA_LISTADO_PAGE_SIZE,
): { items: T[]; page: number; total: number; pageCount: number; pageSize: number } {
  const size = pageSize > 0 ? pageSize : CAJA_LISTADO_PAGE_SIZE;
  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / size) || 1);
  const safePage = total === 0 ? 1 : Math.min(Math.max(1, page), pageCount);
  const start = (safePage - 1) * size;
  return {
    items: items.slice(start, start + size),
    page: safePage,
    total,
    pageCount,
    pageSize: size,
  };
}

export type HistoricoListParams = {
  fechaDesde?: string;
  fechaHasta?: string;
  page_size: number;
  historico: 1;
};

export function historicoQueryParams(
  filter: HistoricoDateFilter,
  todayYmd: string,
): { fechaDesde?: string; fechaHasta?: string; historico: 1 } {
  const clamped = clampHistoricoDateFilter(
    filter.desde || filter.hasta ? filter : defaultHistoricoDateFilter(todayYmd),
    todayYmd,
  );
  let desde = clamped.desde;
  let hasta = clamped.hasta;
  if (!desde && !hasta) {
    const fallback = defaultHistoricoDateFilter(todayYmd);
    desde = fallback.desde;
    hasta = fallback.hasta;
  }
  return {
    ...(desde ? { fechaDesde: desde } : {}),
    ...(hasta ? { fechaHasta: hasta } : {}),
    historico: 1,
  };
}

/**
 * Params de listados de Histórico. `null` = no pedir nada
 * (filtros visibles, sin Buscar todavía). El fetch va solo al click / Enter.
 */
export function historicoFetchParams(
  hasSearched: boolean,
  filter: HistoricoDateFilter,
  todayYmd: string,
  pageSize: number = HISTORICO_PAGE_SIZE,
): HistoricoListParams | null {
  if (!hasSearched) return null;
  return {
    ...historicoQueryParams(filter, todayYmd),
    page_size: pageSize,
  };
}

/** Listado operativo de Movimientos: mes calendario actual. */
export function movimientosMesActualParams(
  todayYmd: string,
  pageSize: number = HISTORICO_PAGE_SIZE,
): { fechaDesde: string; fechaHasta: string; page_size: number } {
  const range = monthRange(todayYmd.slice(0, 7));
  const fallback = defaultHistoricoDateFilter(todayYmd);
  return {
    fechaDesde: range?.desde ?? fallback.desde,
    fechaHasta: range?.hasta ?? todayYmd,
    page_size: pageSize,
  };
}
